import type { FeatureStatus, FeatureResult } from './feature-flags.types';
import {
  VALID_STATUSES,
  SUB_FLAG_PREFIX,
  FEATURE_FLAGS,
  type FeatureFlagKey,
} from './feature-flags.config';

/** Normalize boolean or string status to a `FeatureStatus` string. */
export function normalizeStatus(value: FeatureStatus | boolean): FeatureStatus {
  if (value === true) return 'enabled';
  if (value === false) return 'disabled';
  return value;
}

/**
 * Platform-agnostic resolver. Both web and mobile call this with raw
 * PostHog values; all result shaping lives here.
 *
 * ## Status resolution (priority order)
 * 1. Payload `status` field — highest priority, always wins
 * 2. Variant name — if it matches a valid FeatureStatus value
 * 3. Flag on/off state — on → `'enabled'`, off → `'disabled'`
 *
 * @param key         — flag key from the registry
 * @param flagValue   — raw value from PostHog SDK (`true`/`false`, variant string, or undefined)
 * @param flagPayload — raw JSON payload from PostHog SDK (object or undefined)
 */
export function resolveFeatureFlag(
  key: FeatureFlagKey,
  flagValue: unknown,
  flagPayload: unknown,
): FeatureResult {
  const raw = (
    typeof flagPayload === 'object' && flagPayload !== null ? flagPayload : undefined
  ) as Record<string, unknown> | undefined;
  const { status: rawStatus, ...rest } = raw ?? {};
  const variant = typeof flagValue === 'string' ? flagValue : undefined;

  // Strip reserved keys: `status` (already destructured) and `sub_*`
  const configPayload: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(rest)) {
    if (!k.startsWith(SUB_FLAG_PREFIX)) {
      configPayload[k] = v;
    }
  }
  const payload = Object.keys(configPayload).length > 0 ? configPayload : undefined;

  // 1. Payload status field (highest priority)
  if (typeof rawStatus === 'string' && VALID_STATUSES.includes(rawStatus as FeatureStatus)) {
    const status = rawStatus as FeatureStatus;
    return { isEnabled: status === 'enabled', status, variant, payload };
  }

  // 2. Variant name matches a FeatureStatus
  if (variant && VALID_STATUSES.includes(variant as FeatureStatus)) {
    const status = variant as FeatureStatus;
    return { isEnabled: status === 'enabled', status, variant, payload };
  }

  // 3. Flag on/off state
  const flagIsOn = flagValue === true || typeof flagValue === 'string';
  const flagIsOff = flagValue === false;

  if (flagIsOn) {
    return { isEnabled: true, status: 'enabled', variant, payload };
  }

  if (flagIsOff) {
    return { isEnabled: false, status: 'disabled', variant, payload };
  }

  // Flag not loaded yet — use registry default
  const defaultStatus = normalizeStatus(FEATURE_FLAGS[key].status);
  return { isEnabled: defaultStatus === 'enabled', status: defaultStatus, variant, payload };
}

/** Look up the default status for a sub-flag from the registry. */
function getSubFlagDefault(key: FeatureFlagKey, subFlagKey: string): FeatureStatus {
  const entry = FEATURE_FLAGS[key] as Record<string, unknown>;
  const flagPayload = entry['payload'];
  if (flagPayload && typeof flagPayload === 'object') {
    const map = flagPayload as Record<string, FeatureStatus | boolean>;
    if (subFlagKey in map) return normalizeStatus(map[subFlagKey]);
  }
  return 'enabled';
}

/**
 * Resolve a sub-flag from a parent module's payload.
 *
 * Sub-flags are payload keys prefixed with `sub_` (e.g. `sub_my_orders`).
 * They follow the same value semantics as flags: `true` → enabled, `false` → disabled,
 * or a FeatureStatus string like `"coming_soon"`.
 *
 * ## Rules
 * - Parent not enabled → sub-flag **inherits** parent status (parent wins)
 * - Sub-flag key missing from payload → uses registry default (or `'enabled'` if not declared)
 * - Sub-flag value resolved like a flag: `true`/`false`/FeatureStatus string
 *
 * @param key          — parent flag key (used to look up sub-flag defaults)
 * @param subFlagKey   — sub-flag key (e.g. `"sub_my_orders"`)
 * @param parentResult — resolved `FeatureResult` of the parent flag
 * @param subFlagValue — raw value from the payload (e.g. `true`, `false`, `"coming_soon"`)
 */
export function resolveSubFlag(
  key: FeatureFlagKey,
  subFlagKey: string,
  parentResult: FeatureResult,
  subFlagValue: unknown,
): FeatureResult {
  // Parent not enabled → sub-flag inherits parent status
  if (!parentResult.isEnabled) {
    return { isEnabled: false, status: parentResult.status };
  }

  // Sub-flag key missing from payload → use registry default
  if (subFlagValue === undefined || subFlagValue === null) {
    const defaultStatus = getSubFlagDefault(key, subFlagKey);
    return { isEnabled: defaultStatus === 'enabled', status: defaultStatus };
  }

  if (subFlagValue === true) {
    return { isEnabled: true, status: 'enabled' };
  }

  if (subFlagValue === false) {
    return { isEnabled: false, status: 'disabled' };
  }

  if (typeof subFlagValue === 'string' && VALID_STATUSES.includes(subFlagValue as FeatureStatus)) {
    const status = subFlagValue as FeatureStatus;
    return { isEnabled: status === 'enabled', status };
  }

  // Unknown value → default to enabled
  return { isEnabled: true, status: 'enabled' };
}
